/** @jsxImportSource theme-ui */
import React, { useState, useEffect, memo } from "react";
import { Box, Button, Container, Spinner, Text } from "theme-ui";
import Logo from "../components/Logo";
import apiInvocation from "../api/apiInvocation";
import { UserData } from "../types/userdata";
import { AdminNotification } from "../types/adminnotification";
import Tooltip from "../components/common/Tooltip";
import Icon from "../components/common/Icon";
import { useNavigate } from "react-router-dom";
import EmailModal from "../components/EmailModal";
import { message } from "antd";

// Table Header Component
const TableHeader = memo(() => (
    <thead>
        <tr>
            <th>Created By</th>
            <th>Submissions Count</th>
            <th>Total Rating</th>
        </tr>
    </thead>
));

// Table Row Component
const TableRow = memo(({ item }: { item: UserData }) => (
    <tr>
        <td>
            {item.createdByUser}
            {item.isAdmin && (
                <sup
                    sx={{
                        fontSize: "10px",
                        color: "primary",
                        marginLeft: "4px",
                    }}
                >
                    ADMIN
                </sup>
            )}
        </td>
        <td>{item.requestCount}</td>
        <td>{item.totalRating}</td>
    </tr>
));

// Table Body Component
const TableBody = memo(({ data }: { data: UserData[] }) => (
    <tbody>
        {data.length > 0 ? (
            data.map((item, index) => <TableRow key={index} item={item} />)
        ) : (
            <tr>
                <td colSpan={3} sx={{ textAlign: "center" }}>
                    No data available
                </td>
            </tr>
        )}
    </tbody>
));

// Pagination Component
const Pagination = memo(({ pageNumber, pageSize, totalCount, onPrevious, onNext }: any) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 4,
        }}
    >
        <Button
            onClick={onPrevious}
            disabled={pageNumber === 1}
            sx={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bg: "secondary",
                color: "white",
                fontSize: 6,
                fontWeight: "bold",
                mx: 2,
                "&:disabled": {
                    bg: "muted",
                    cursor: "not-allowed",
                    color: "white",
                },
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                transition: "all 0.3s ease",
                "&:hover:not(:disabled)": {
                    bg: "primary",
                },
            }}
        >
            {"<"}
        </Button>
        <Text
            sx={{
                mx: 3,
                fontSize: [3, 4],
                color: "secondaryText",
            }}
        >
            Page {pageNumber}
        </Text>
        <Button
            onClick={onNext}
            disabled={pageNumber * pageSize >= totalCount}
            sx={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bg: "secondary",
                color: "white",
                fontSize: 6,
                fontWeight: "bold",
                mx: 2,
                "&:disabled": {
                    bg: "muted",
                    cursor: "not-allowed",
                    color: "white",
                },
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                transition: "all 0.3s ease",
                "&:hover:not(:disabled)": {
                    bg: "primary",
                },
            }}
        >
            {">"}
        </Button>
    </Box>
));

const Users: React.FC = () => {
    const [data, setData] = useState<UserData[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [adminNotifications, setAdminNotifications] = useState<AdminNotification[]>([]);
    const [notificationsLoading, setNotificationsLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const fetchData = async (page: number) => {
        setLoading(true);
        try {
            const response = await apiInvocation.getUsersData(page, pageSize);
            setData(response.data);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAdminNotifications = async () => {
        setNotificationsLoading(true);
        try {
            const result = await apiInvocation.getAdminNotifications();
            setAdminNotifications(result);
        } catch (error) {
            console.error("Error fetching admin notifications:", error);
        } finally {
            setNotificationsLoading(false);
        }
    };

    useEffect(() => {
        fetchData(pageNumber);
        fetchAdminNotifications();
    }, [pageNumber]);

    const handleNext = () => {
        if (pageNumber * pageSize < totalCount) {
            setPageNumber((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (pageNumber > 1) {
            setPageNumber((prev) => prev - 1);
        }
    };

    const handleToggleNotification = async (admin: AdminNotification) => {
        const updated = !admin.notificationsEnabled;
        setAdminNotifications((prev) =>
            prev.map((a) => (a.userId === admin.userId ? { ...a, notificationsEnabled: updated } : a))
        );
        try {
            await apiInvocation.updateAdminNotification(admin.userId, admin.userName, admin.email, updated);
        } catch {
            setAdminNotifications((prev) =>
                prev.map((a) => (a.userId === admin.userId ? { ...a, notificationsEnabled: !updated } : a))
            );
            message.error("Failed to update notification setting.");
        }
    };

    const handleDisableAll = async () => {
        setAdminNotifications((prev) => prev.map((a) => ({ ...a, notificationsEnabled: false })));
        try {
            await apiInvocation.disableAllAdminNotifications();
            message.success("All notifications disabled.");
        } catch {
            fetchAdminNotifications();
            message.error("Failed to disable all notifications.");
        }
    };

    return (
        <Container
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minHeight: "100vh",
                overflowY: "auto",
                px: 4,
                py: 3,
            }}
        >
            <Logo />

            {totalCount !== 0 && (
                <Text
                    sx={{
                        fontSize: [3, 5],
                        color: "secondaryText",
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    Total number of users: {totalCount}
                </Text>
            )}

            {loading ? (
                <Spinner
                    size={48}
                    sx={{
                        color: "primary",
                        mt: 4,
                    }}
                />
            ) : (
                <>
                    <Box
                        sx={{
                            position: "relative",
                            marginTop: "10px",
                            width: "100%",
                            maxWidth: "800px",
                            overflowX: "auto",
                            border: "2px solid",
                            borderColor: "muted",
                            borderRadius: "8px",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                        }}
                    >
                        <Box
                            as="table"
                            sx={{
                                width: "100%",
                                borderCollapse: "collapse",
                                "& th, & td": {
                                    textAlign: "left",
                                    border: "none",
                                    padding: "12px",
                                    fontSize: [1, 2],
                                },
                                "& th": {
                                    bg: "#11B473",
                                    color: "white",
                                    textTransform: "uppercase",
                                },
                                "& td": {
                                    color: "text",
                                },
                            }}
                        >
                            <TableHeader />
                            <TableBody data={data} />
                        </Box>
                    </Box>

                    <Pagination
                        pageNumber={pageNumber}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                    />
                </>
            )}

            {/* Admin Email Notifications Section */}
            <Box
                sx={{
                    mt: 5,
                    width: "100%",
                    maxWidth: "800px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Text sx={{ fontSize: [3, 4], color: "secondaryText", fontWeight: "bold" }}>
                        Admin Email Notifications
                    </Text>
                    <Button
                        onClick={handleDisableAll}
                        sx={{
                            bg: "muted",
                            color: "text",
                            fontSize: 1,
                            px: 3,
                            py: 2,
                            borderRadius: "6px",
                            cursor: "pointer",
                            "&:hover": { bg: "secondary", color: "white" },
                        }}
                    >
                        Disable All
                    </Button>
                </Box>

                {notificationsLoading ? (
                    <Spinner size={32} sx={{ color: "primary" }} />
                ) : adminNotifications.length === 0 ? (
                    <Text sx={{ color: "secondaryText", fontSize: 1 }}>No admins found.</Text>
                ) : (
                    <Box
                        sx={{
                            border: "2px solid",
                            borderColor: "muted",
                            borderRadius: "8px",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                            overflow: "hidden",
                        }}
                    >
                        {adminNotifications.map((admin, index) => (
                            <Box
                                key={admin.userId}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 3,
                                    py: 3,
                                    bg: index % 2 === 0 ? "background" : "muted",
                                    borderBottom: index < adminNotifications.length - 1 ? "1px solid" : "none",
                                    borderColor: "muted",
                                }}
                            >
                                <Box>
                                    <Text sx={{ fontSize: 2, color: "text", fontWeight: "bold" }}>
                                        {admin.userName}
                                    </Text>
                                    <Text sx={{ fontSize: 1, color: "secondaryText" }}>{admin.email}</Text>
                                </Box>
                                <Box
                                    onClick={() => handleToggleNotification(admin)}
                                    sx={{
                                        width: "48px",
                                        height: "26px",
                                        borderRadius: "13px",
                                        bg: admin.notificationsEnabled ? "#11B473" : "#b0b0b0",
                                        border: "2px solid",
                                        borderColor: admin.notificationsEnabled ? "#0d9c63" : "#909090",
                                        position: "relative",
                                        cursor: "pointer",
                                        transition: "background-color 0.2s ease",
                                        flexShrink: 0,
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "3px",
                                            left: admin.notificationsEnabled ? "25px" : "3px",
                                            width: "20px",
                                            height: "20px",
                                            borderRadius: "50%",
                                            bg: "white",
                                            transition: "left 0.2s ease",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                        }}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            <Box sx={{ mt: 13 }} />

            <Box
                sx={{
                    position: "fixed",
                    bottom: [20, 60],
                    right: [20, 60],
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box>
                    <Tooltip content="Invite New User">
                        <Button
                            variant="primary"
                            sx={{
                                width: 60,
                                height: 60,
                                border: "2px solid #F5F9FC",
                            }}
                        >
                            <Icon
                                name="user-plus"
                                color="buttonText"
                                size={24}
                                onClick={() => setIsEmailModalOpen(true)}
                            />
                        </Button>
                    </Tooltip>
                    <Tooltip content="Home page">
                        <Button
                            variant="primary"
                            sx={{
                                width: 60,
                                height: 60,
                                border: "2px solid #F5F9FC",
                            }}
                        >
                            <Icon
                                name="home"
                                color="buttonText"
                                size={24}
                                onClick={() => navigate("/")}
                            />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
            <EmailModal
                isOpen={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
            />
        </Container>
    );
};

export default Users;
